import React, {useEffect} from 'react';
import {useIntl} from 'react-intl';
import {withRouter, useHistory} from 'react-router';
import {createFragmentContainer, graphql} from 'react-relay';
import * as tracking from '../../../tracking';
import Util from '../../../forecast-app/shared/util/util';
import {getProjectHealthTopHeader} from './ProjectHealthHeaders';
import {ProjectHealthContentSection, ProjectHealthPageWrapper} from './project_health_styles/ProjectHealthPage.styled';
import ProjectHealthStatusSection from './ProjectHealthStatusSection';
import {ProjectHealthTableSection} from './ProjectHealthTableSection';
import {
	getFocusData,
	getPerformanceData,
	getPredictedEndDateData,
	getBudgetData,
	getSuggestedStatus,
} from './ProjectHealthLogic';
import {BUDGET_TYPE, PROJECT_STATUS} from '../../../constants';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

const ProjectHealthPage = ({viewer}) => {
	const intl = useIntl();
	const history = useHistory();
	const {project} = viewer;
	const {projectHealth} = project;

	const projectEndDate =
		project.projectEndYear &&
		Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay);
	const data = [
		getPredictedEndDateData(project, projectEndDate, projectHealth.predictedEndDateError, intl),
		getPerformanceData(project, projectHealth.performanceRolling, projectHealth.performanceError, intl),
		getFocusData(project, projectHealth.focus, projectHealth.focusError, intl),
	];

	if (project.budgetType === BUDGET_TYPE.FIXED_PRICE && hasPermission(PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION)) {
		data.push(getBudgetData(project, history, intl));
	}

	const warnings = data.map(insight => insight.warningVariant);
	const suggestedStatus = getSuggestedStatus(warnings);

	const projectHalted = project.status === PROJECT_STATUS.HALTED;

	useEffect(() => {
		const name =
			viewer.project.name !== null && viewer.project.name !== ''
				? viewer.project.name
				: 'P-' + viewer.project.companyProjectId;
		document.title = 'Nova Insights - ' + name + ' - Forecast';

		const availableInsights = data.filter(insight => !insight.disabled).map(insight => insight.title);
		tracking.trackPage('nova-insights', null, null, {'available-insights': availableInsights});
		return () => {
			tracking.trackPageLeft('nova-insights');
		};
	}, []);

	return (
		<ProjectHealthPageWrapper data-cy="project-health-page">
			{getProjectHealthTopHeader(viewer.email, intl)}
			<ProjectHealthContentSection>
				<ProjectHealthTableSection data={data} projectHalted={projectHalted}></ProjectHealthTableSection>
				<ProjectHealthStatusSection
					project={viewer.project}
					suggestedStatus={suggestedStatus}
				></ProjectHealthStatusSection>
			</ProjectHealthContentSection>
		</ProjectHealthPageWrapper>
	);
};

const ProjectHealthPageQuery = graphql`
	query ProjectHealthPage_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "project_health")
			project(id: $projectId) {
				id
				projectGroupId
				estimationUnit
			}
			...ProjectHealthPage_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {ProjectHealthPageQuery};

export default withRouter(
	createFragmentContainer(ProjectHealthPage, {
		viewer: graphql`
			fragment ProjectHealthPage_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}) {
				id
				email
				project(id: $projectId) {
					id
					name
					status
					companyProjectId
					projectStartYear
					projectStartMonth
					projectStartDay
					projectEndYear
					projectEndMonth
					projectEndDay
					budget
					budgetType
					revenueMarginLow
					revenueMarginHigh
					rateCard {
						currency
					}
					projectHealth {
						performanceRolling
						optimisticPerformanceRolling
						conservativePerformanceRolling
						performanceError
						focus
						focusError
						predictedEndDate
						predictedEndDateError
						optimisticPredictedEndDate
					}
					financialNumbers(convertToProjectCurrency: true) {
						forecastRevenue
					}
					...ProjectHealthStatusSection_project
					...PredictedEndDateModal_project
				}
			}
		`,
	})
);
